// Pure helpers for the live board: grouping, ordering and wording only. Eligibility
// is the server's (evaluated at read time); nothing here decides it. Relative
// imports only (tests/unit/shell-board.test.ts runs without the '@/' alias).
import type { AreaRef, BoardEntry, Eligibility } from '../../domain/types'
import { fmtDate, fmtTime } from '../../lib/format'

export type BoardAlert = { label: string; tone: 'bad' | 'warn'; rank: number }

/**
 * Label for the person who validated the current cycle (BoardEntry.aa keeps its historic key).
 * From 0016 the RS validates; a validation made before 0016 reads "Area Authority (historic)".
 */
export function validatorLabel(e: Pick<BoardEntry, 'validator_role'>): string {
  if (e.validator_role === 'AA') return 'Area Authority (historic)'
  if (e.validator_role === 'RS') return 'Validated by (RS)'
  return 'Validated by'
}

/** The board's OM approval badge, or null when the permit's policy has no OM step (or the server is older). */
export function boardOmApproval(e: Pick<BoardEntry, 'om_approval'>): { label: string; tone: 'ok' | 'warn' } | null {
  if (e.om_approval === 'approved') return { label: 'OM approved', tone: 'ok' }
  if (e.om_approval === 'pending') return { label: 'OM approval pending - no work can start', tone: 'warn' }
  return null
}

/**
 * The board's extension status (0020): a badge while a request waits for the RS or once the
 * permit runs on an RS-approved extension, and the current authorised end ("05 Nov 2026 19:00
 * GST"). Nothing on an older server.
 */
export function boardExtension(e: Pick<BoardEntry, 'authorisation_to' | 'extended' | 'extension_pending'>): {
  badge: { label: string; tone: 'ok' | 'warn' | 'info' } | null
  until: string | null
} {
  const until = e.authorisation_to ? `${fmtDate(e.authorisation_to)} ${fmtTime(e.authorisation_to)} GST${e.extended ? ' (extended)' : ''}` : null
  if (e.extension_pending) return { badge: { label: 'Extension requested', tone: 'warn' }, until }
  if (e.extended) return { badge: { label: 'Extended', tone: 'info' }, until }
  return { badge: null, until }
}

/** Stops and suspensions are highlighted and listed first in their area. */
export function boardAlert(e: Pick<BoardEntry, 'cycle' | 'lifecycle_state'>): BoardAlert | null {
  if (e.cycle?.cycle_state === 'suspension_reported') return { label: 'Stop reported', tone: 'bad', rank: 0 }
  if (e.cycle?.cycle_state === 'suspended') return { label: 'Suspended', tone: 'warn', rank: 1 }
  if (e.lifecycle_state === 'cancellation_signed') return { label: 'Cancelled by PH - awaiting closeout', tone: 'warn', rank: 2 }
  return null
}

function rank(e: BoardEntry): number {
  return boardAlert(e)?.rank ?? 3
}

export type AreaGroup = { area: AreaRef; entries: BoardEntry[]; stops: number; suspended: number }

/** Groups entries by area (areas by name), alerts first within each area, then by permit number. */
export function groupBoardByArea(entries: readonly BoardEntry[], areaId = ''): AreaGroup[] {
  const groups = new Map<string, AreaGroup>()
  for (const e of entries) {
    if (areaId && e.site_area.id !== areaId) continue
    let g = groups.get(e.site_area.id)
    if (!g) {
      g = { area: e.site_area, entries: [], stops: 0, suspended: 0 }
      groups.set(e.site_area.id, g)
    }
    g.entries.push(e)
    if (e.cycle?.cycle_state === 'suspension_reported') g.stops += 1
    if (e.cycle?.cycle_state === 'suspended') g.suspended += 1
  }
  const out = [...groups.values()]
  for (const g of out) g.entries.sort((a, b) => rank(a) - rank(b) || a.permit_number.localeCompare(b.permit_number))
  return out.sort((a, b) => a.area.name.localeCompare(b.area.name) || a.area.code.localeCompare(b.area.code))
}

export function areaOptions(entries: readonly BoardEntry[]): AreaRef[] {
  const seen = new Map<string, AreaRef>()
  for (const e of entries) if (!seen.has(e.site_area.id)) seen.set(e.site_area.id, e.site_area)
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
}

export function boardCounts(entries: readonly BoardEntry[]): { total: number; stops: number; suspended: number; mayProceed: number } {
  return {
    total: entries.length,
    stops: entries.filter((e) => e.cycle?.cycle_state === 'suspension_reported').length,
    suspended: entries.filter((e) => e.cycle?.cycle_state === 'suspended').length,
    mayProceed: entries.filter((e) => e.eligibility.status === 'recorded_conditions_satisfied').length,
  }
}

/** "03 Nov 2026, 07:00 to 19:00 GST" (Muscat time), or null when the cycle is not validated. */
export function validationWindowText(from: string | null | undefined, to: string | null | undefined): string | null {
  if (!from && !to) return null
  if (from && to) {
    if (fmtDate(from) === fmtDate(to)) return `${fmtDate(from)}, ${fmtTime(from)} to ${fmtTime(to)} GST`
    return `${fmtDate(from)} ${fmtTime(from)} to ${fmtDate(to)} ${fmtTime(to)} GST`
  }
  if (to) return `until ${fmtDate(to)} ${fmtTime(to)} GST`
  return `from ${fmtDate(from)} ${fmtTime(from)} GST`
}

/** The first reason the server gave, and how many more there are. */
export function firstBlocker(e: Eligibility): { blocker: Eligibility['blockers'][number]; more: number } | null {
  if (e.blockers.length === 0) return null
  return { blocker: e.blockers[0], more: e.blockers.length - 1 }
}
